import { Shield, Menu, X, Bell, LogOut, User, LayoutDashboard, CreditCard, ArrowLeftRight, Settings, ShieldAlert, Sun, Moon, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NAV_LINKS, ROUTE_PATHS } from '@/lib/index';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useI18n } from '@/hooks/useI18n';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, profile, signOut } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const { lang, setLang, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === '/';

  const handleSignOut = async () => {
    await signOut();
    navigate(ROUTE_PATHS.HOME);
  };

  const initials = profile
    ? `${profile.first_name?.[0] ?? ''}${profile.last_name?.[0] ?? ''}`.toUpperCase() || 'U'
    : 'U';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border/60">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
              <div className="relative">
                <Shield className="w-9 h-9 text-gold" strokeWidth={2} />
                <div className="absolute inset-0 bg-gold/15 blur-xl group-hover:bg-gold/25 transition-all rounded-full" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-foreground tracking-tight leading-tight">CYF24 Bank</span>
                <span className="text-[10px] text-muted-foreground font-mono tracking-wider">British Digital Banking</span>
              </div>
            </Link>

            {isHome && (
              <nav className="hidden lg:flex items-center gap-7">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      const target = document.querySelector(link.href);
                      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="nav-link text-sm font-medium cursor-pointer"
                  >
                    {t(link.labelKey)}
                  </a>
                ))}
              </nav>
            )}

            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-1 bg-muted/60 rounded-xl px-1 py-1 border border-border/50">
                <Globe className="w-3.5 h-3.5 text-muted-foreground mx-1" />
                <button
                  onClick={() => setLang('en')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    lang === 'en' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang('pl')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    lang === 'pl' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  PL
                </button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="w-9 h-9 rounded-xl hover:bg-muted transition-colors"
                title={isDark ? t('theme_light') : t('theme_dark')}
              >
                {isDark ? (
                  <Sun className="w-4.5 h-4.5 text-gold" />
                ) : (
                  <Moon className="w-4.5 h-4.5 text-muted-foreground" />
                )}
              </Button>

              {user ? (
                <>
                  <Button variant="ghost" size="icon" className="w-9 h-9 rounded-xl" asChild>
                    <Link to={ROUTE_PATHS.DASHBOARD_NOTIFICATIONS}>
                      <Bell className="w-4.5 h-4.5" />
                    </Link>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center gap-2 px-3 h-9 rounded-xl">
                        <Avatar className="w-7 h-7">
                          <AvatarFallback className="bg-gold/20 text-gold text-xs font-bold border border-gold/30">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <span className="hidden md:block text-sm font-medium">
                          {profile ? `${profile.first_name} ${profile.last_name}` : t('layout_client_panel')}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem asChild>
                        <Link to={ROUTE_PATHS.DASHBOARD} className="cursor-pointer">
                          <LayoutDashboard className="w-4 h-4 mr-2" /> {t('dash_title')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={ROUTE_PATHS.DASHBOARD_TRANSACTIONS} className="cursor-pointer">
                          <ArrowLeftRight className="w-4 h-4 mr-2" /> {t('tx_title')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={ROUTE_PATHS.DASHBOARD_CARDS} className="cursor-pointer">
                          <CreditCard className="w-4 h-4 mr-2" /> {t('cards_title')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={ROUTE_PATHS.DASHBOARD_PROFILE} className="cursor-pointer">
                          <User className="w-4 h-4 mr-2" /> {t('profile_title')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={ROUTE_PATHS.DASHBOARD_SETTINGS} className="cursor-pointer">
                          <Settings className="w-4 h-4 mr-2" /> {t('settings_title')}
                        </Link>
                      </DropdownMenuItem>
                      {profile?.is_admin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to={ROUTE_PATHS.ADMIN} className="cursor-pointer text-amber-500 focus:text-amber-500">
                              <ShieldAlert className="w-4 h-4 mr-2" /> {t('layout_admin_panel')}
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                        <LogOut className="w-4 h-4 mr-2" /> {t('sign_out')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="hidden lg:flex items-center gap-2">
                  <Button variant="ghost" size="sm" asChild className="rounded-xl">
                    <Link to={ROUTE_PATHS.LOGIN}>{t('nav_login')}</Link>
                  </Button>
                  <Button
                    size="sm" asChild
                    className="btn-gold rounded-xl font-semibold px-5"
                  >
                    <Link to={ROUTE_PATHS.REGISTER}>{t('nav_open_account')}</Link>
                  </Button>
                </div>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden w-9 h-9 rounded-xl"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-card border-t border-border/50 px-4 py-5 space-y-2">
            {isHome && NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block text-sm font-medium text-muted-foreground hover:text-foreground py-2.5 px-3 rounded-lg hover:bg-muted/60 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileOpen(false);
                  setTimeout(() => {
                    const target = document.querySelector(link.href);
                    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
              >
                {t(link.labelKey)}
              </a>
            ))}

            <div className="pt-3 border-t border-border/40 flex items-center justify-between gap-3">
              <div className="flex items-center gap-1 bg-muted/60 rounded-xl px-1 py-1 border border-border/50">
                <Globe className="w-3.5 h-3.5 text-muted-foreground mx-1" />
                <button
                  onClick={() => setLang('en')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    lang === 'en' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang('pl')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    lang === 'pl' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  PL
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleTheme}
                className="flex items-center gap-2 rounded-xl text-xs font-semibold"
              >
                {isDark ? (
                  <><Sun className="w-3.5 h-3.5 text-gold" /> {t('theme_light')}</>
                ) : (
                  <><Moon className="w-3.5 h-3.5" /> {t('theme_dark')}</>
                )}
              </Button>
            </div>

            {!user && (
              <div className="pt-2 flex gap-2">
                <Button variant="outline" size="sm" asChild className="flex-1 rounded-xl">
                  <Link to={ROUTE_PATHS.LOGIN} onClick={() => setMobileOpen(false)}>{t('nav_login')}</Link>
                </Button>
                <Button size="sm" asChild className="flex-1 btn-gold rounded-xl">
                  <Link to={ROUTE_PATHS.REGISTER} onClick={() => setMobileOpen(false)}>{t('nav_open_account')}</Link>
                </Button>
              </div>
            )}
            {user && (
              <div className="pt-2 space-y-1">
                <Button variant="ghost" size="sm" asChild className="w-full justify-start rounded-xl">
                  <Link to={ROUTE_PATHS.DASHBOARD} onClick={() => setMobileOpen(false)}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t('dash_title')}
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start rounded-xl text-destructive hover:text-destructive" onClick={() => { handleSignOut(); setMobileOpen(false); }}>
                  <LogOut className="w-4 h-4 mr-2" /> {t('sign_out')}
                </Button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">{children}</main>

      <footer className="bg-card border-t border-border/50">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">

            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-7 h-7 text-gold" />
                <span className="text-lg font-bold text-foreground">CYF24 Bank</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {t('footer_description')}
              </p>
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  <span className="text-foreground font-medium">CYF SOLUTION LTD</span> · {t('footer_company_number')}: <span className="font-mono text-gold">15936230</span>
                </p>
                <p className="text-xs text-muted-foreground">{t('footer_registered')}</p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">{t('footer_products')}</h3>
              <ul className="space-y-2.5">
                <li><a href="#plans" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t('footer_personal_banking')}</a></li>
                <li><a href="#services" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t('footer_business_banking')}</a></li>
                <li><a href="#services" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t('footer_investments')}</a></li>
                <li><a href="#services" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t('footer_crypto')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">{t('footer_legal')}</h3>
              <ul className="space-y-2.5">
                <li>
                  <Link to={ROUTE_PATHS.LEGAL} className="text-sm text-muted-foreground hover:text-gold transition-colors">
                    {t('footer_legal_info')}
                  </Link>
                </li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t('footer_terms')}</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t('footer_privacy')}</a></li>
                <li><a href="#faq" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t('footer_faq')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">{t('footer_contact')}</h3>
              <ul className="space-y-2.5">
                <li className="text-sm text-muted-foreground">25 Canada Square</li>
                <li className="text-sm text-muted-foreground">London E14 5LQ, UK</li>
                <li>
                  <a href="mailto:support@cyf24.com" className="text-sm text-muted-foreground hover:text-gold transition-colors">
                    support@cyf24.com
                  </a>
                </li>
                <li>
                  <a href="tel:+447520634020" className="text-sm text-muted-foreground hover:text-gold transition-colors">
                    +44 7520 634 020
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/50 pt-8">
            <div className="flex flex-wrap gap-4 items-start justify-between">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('footer_fscs')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('footer_fca_fscs')}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t('footer_copyright')}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}